import { useEffect, useRef } from "react";

export const MoleculeCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.7 + 0.7,
      vx: (Math.random() - 0.5) * 0.0007,
      vy: (Math.random() - 0.5) * 0.0007,
    }));

    let t = 0;
    const draw = () => {
      t += 0.004;
      ctx.clearRect(0, 0, w, h);
      const cx = w * 0.5 + mouse.current.x * 22;
      const cy = h * 0.5 + mouse.current.y * 22;
      const R = Math.min(w, h) * 0.34;

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
      }
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const ax = particles[i].x * w;
          const ay = particles[i].y * h;
          const bx = particles[j].x * w;
          const by = particles[j].y * h;
          const d = Math.hypot(ax - bx, ay - by);
          if (d < 90) {
            ctx.strokeStyle = `rgba(240, 128, 111, ${0.14 * (1 - d / 90)})`;
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
          }
        }
      }
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(224, 90, 71, 0.35)";
        ctx.fill();
      }

      for (let k = 0; k < 3; k++) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate((k * Math.PI) / 3 + t * 0.12);
        ctx.beginPath();
        ctx.ellipse(0, 0, R, R * 0.4, 0, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(240, 128, 111, 0.45)";
        ctx.stroke();
        const a = t * (1.1 + k * 0.35) + k * 2.1;
        const ex = Math.cos(a) * R;
        const ey = Math.sin(a) * R * 0.4;
        ctx.beginPath();
        ctx.arc(ex, ey, 3.4, 0, Math.PI * 2);
        ctx.fillStyle = "#E05A47";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(ex, ey, 7, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(224, 90, 71, 0.18)";
        ctx.fill();
        ctx.restore();
      }

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 26);
      grad.addColorStop(0, "#E05A47");
      grad.addColorStop(1, "rgba(224, 90, 71, 0)");
      ctx.beginPath();
      ctx.arc(cx, cy, 26, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 8, 0, Math.PI * 2);
      ctx.fillStyle = "#E05A47";
      ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = {
        x: (e.clientX - rect.left) / rect.width - 0.5,
        y: (e.clientY - rect.top) / rect.height - 0.5,
      };
    };
    window.addEventListener("mousemove", onMove);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-testid="molecule-canvas"
      aria-hidden="true"
    />
  );
};
